import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, Check, ClipboardList, Star, Info, X } from 'lucide-react';
import { useNotificationStore, type Notification } from '../store/useNotificationStore';
import { usePlanStore } from '../store/usePlanStore';

function timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diff = Math.floor((now - then) / 1000);

    if (diff < 60) return 'teraz';
    if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} dni temu`;
    return new Date(dateStr).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
    survey_response: <ClipboardList size={14} className="text-brand-500" />,
    promo: <Star size={14} className="text-amber-500" />,
    system: <Info size={14} className="text-blue-500" />,
};

/**
 * Bell icon as inline SVG (lucide-react v0.263 doesn't have Bell export)
 */
function BellIcon({ size = 20 }: { size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
    );
}

export default function NotificationBell() {
    const {
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        startPolling,
        stopPolling,
    } = useNotificationStore();

    const setActiveTab = usePlanStore((s) => s.setActiveTab);

    const [open, setOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Start polling on mount
    useEffect(() => {
        startPolling();
        return () => stopPolling();
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleClick = (n: Notification) => {
        if (!n.read) markAsRead(n.id);

        // Navigate to survey responses if it's a survey_response notification
        if (n.type === 'survey_response' && n.link) {
            setActiveTab('survey');
        }
        setOpen(false);
    };

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell button */}
            <button
                onClick={() => {
                    setOpen(!open);
                    if (!open) fetchNotifications(); // Refresh on open
                }}
                className="relative p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                aria-label="Powiadomienia"
            >
                <BellIcon size={20} />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-lg animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[480px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="text-sm font-bold">Powiadomienia</h3>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <button
                                    onClick={() => markAllAsRead()}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
                                    title="Oznacz wszystkie jako przeczytane"
                                >
                                    <Check size={12} /> Przeczytaj wszystkie
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Notification list */}
                    <div className="overflow-y-auto max-h-[400px] divide-y divide-slate-100 dark:divide-slate-800">
                        {notifications.length === 0 ? (
                            <div className="text-center py-12 px-4">
                                <BellIcon size={28} />
                                <p className="text-sm text-slate-400 mt-2">Brak powiadomień</p>
                            </div>
                        ) : (
                            notifications.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => handleClick(n)}
                                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition ${!n.read ? 'bg-brand-50/50 dark:bg-brand-950/20' : ''
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5 flex-shrink-0">
                                            {TYPE_ICONS[n.type] || TYPE_ICONS.system}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`text-xs font-semibold truncate ${!n.read ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                                                    }`}>
                                                    {n.title}
                                                </p>
                                                {!n.read && (
                                                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-brand-500" />
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                                                {n.message}
                                            </p>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {timeAgo(n.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
